import type { OptionType } from "../../../../types/types";
import type {
  AttributeSchema,
  AttributeValue,
} from "../../../../types/types.PIM";
import type { ProductGroupFormValues } from "./product_overview.utils";
import {
  isExistingAttributeFormValue,
  processAttributeFormValue,
} from "./product_overview.utils";

type ExistingAttributeMap = Record<string, AttributeValue | AttributeValue[]>;

type PatchValue =
  | string
  | boolean
  | Record<string, unknown>
  | { display_text: string; url: string };

type ComparableFormValue = Exclude<
  ProductGroupFormValues[string],
  OptionType<string>[] | string[]
>;

type PatchEntry = {
  attribute_id: string;
  value: PatchValue;
};

type PatchContext = {
  attribute: AttributeSchema;
  existingValues: AttributeValue[];
  mode: "add" | "edit";
};

const getComparableValue = (value: unknown) => {
  if (typeof value !== "object" || value === null) {
    return value;
  }

  if ("asset_id" in value) {
    return (value as { asset_id?: string }).asset_id;
  }

  if ("name" in value) {
    return (value as { name?: string }).name;
  }

  if ("id" in value) {
    return (value as { id?: string }).id;
  }

  if ("value" in value) {
    return (value as { value?: string }).value;
  }

  return value;
};

const getPatchValue = (value: unknown) => {
  if (typeof value !== "object" || value === null) {
    return value;
  }

  if ("asset_id" in value && (value as { asset_id?: string }).asset_id) {
    return { asset_id: (value as { asset_id: string }).asset_id };
  }

  if ("name" in value && (value as { name?: string }).name) {
    return (value as { name?: string }).name;
  }

  if ("id" in value && (value as { id?: string }).id) {
    return (value as { id?: string }).id;
  }

  if ("value" in value) {
    return (value as { value?: string }).value;
  }

  return value;
};

const normalizeBooleanFormValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => normalizeBooleanFormValue(item));
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "on" || normalized === "1";
  }
  if (typeof value === "number") {
    return value === 1;
  }
  return Boolean(value);
};

export const isFormValueEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "" || value === " ") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.values(value).every((item) => isFormValueEmpty(item));
  }

  return false;
};

const buildPatchData = (
  formValues: Record<string, unknown>,
  getContext: (attribute_id: string) => PatchContext | null
) => {
  const values: PatchEntry[] = [];
  const values_to_delete: string[] = [];

  Object.entries(formValues).forEach(([attribute_id, formValue]) => {
    if (attribute_id === "display_text" || attribute_id === "") {
      return;
    }

    const context = getContext(attribute_id);
    if (!context) {
      return;
    }

    const { attribute, existingValues, mode } = context;

    if (
      attribute.input_type === "toggle" ||
      attribute.input_type === "checkbox"
    ) {
      const booleanValue = normalizeBooleanFormValue(formValue);
      const existing = existingValues[0]?.value;

      if (
        existingValues.length === 0 ||
        !isExistingAttributeFormValue(
          booleanValue,
          existing,
          attribute.input_type
        )
      ) {
        values.push({
          attribute_id: attribute.id,
          value: booleanValue,
        });
      }
      return;
    }

    if (mode === "add") {
      if (Array.isArray(formValue)) {
        values.push(
          ...(formValue as unknown[]).map((value) => ({
            attribute_id: attribute.id,
            value: getPatchValue(value) as PatchValue,
          }))
        );
      } else {
        const updatedValue =
          attribute.input_type === "date"
            ? processAttributeFormValue(
                formValue as Exclude<
                  ProductGroupFormValues[string],
                  OptionType<string>[] | string[]
                >,
                attribute.input_type
              )
            : getPatchValue(formValue);

        if (!isFormValueEmpty(updatedValue)) {
          values.push({
            attribute_id: attribute.id,
            value: updatedValue as PatchValue,
          });
        }
      }
      return;
    }

    if (isFormValueEmpty(formValue)) {
      values_to_delete.push(
        ...existingValues
          .filter(({ value }) => !isFormValueEmpty(value))
          .map(({ id }) => id)
      );
      return;
    }

    if (Array.isArray(formValue)) {
      const newValues = (formValue as unknown[])
        .filter(
          (value) =>
            !existingValues.some((existing) =>
              isExistingAttributeFormValue(
                getComparableValue(value) as ComparableFormValue,
                existing.value,
                attribute.input_type
              )
            )
        )
        .map((value) => ({
          attribute_id: attribute.id,
          value: getPatchValue(value) as PatchValue,
        }));
      values.push(...newValues);

      const valuesToDelete = existingValues
        .filter(
          ({ value }) =>
            !(formValue as unknown[]).some((newValue) =>
              isExistingAttributeFormValue(
                getComparableValue(newValue) as ComparableFormValue,
                value,
                attribute.input_type
              )
            )
        )
        .map(({ id }) => id);
      values_to_delete.push(...valuesToDelete);
      return;
    }

    const processed = processAttributeFormValue(
      formValue as Exclude<
        ProductGroupFormValues[string],
        OptionType<string>[] | string[]
      >,
      attribute.input_type
    );
    const existing = existingValues[0]?.value;

    if (
      attribute.input_type === "link" &&
      processed == null &&
      existingValues.length
    ) {
      values_to_delete.push(...existingValues.map(({ id }) => id));
      return;
    }

    if (
      processed !== null &&
      !isExistingAttributeFormValue(
        processed as ComparableFormValue,
        existing,
        attribute.input_type
      )
    ) {
      values.push({
        attribute_id: attribute.id,
        value: processed,
      });
    }
  });

  return {
    values,
    values_to_delete,
  };
};

export const buildAttributesPatchData = (
  formValues: ProductGroupFormValues,
  attributesMap: Record<string, AttributeSchema>
) =>
  buildPatchData(formValues, (attribute_id) => {
    const attribute = attributesMap[attribute_id];
    if (
      !attribute ||
      !attribute.is_editable ||
      attribute.is_not_applicable === true
    ) {
      return null;
    }

    return {
      attribute,
      existingValues: attribute.values ?? [],
      mode: "edit",
    };
  });

export const buildCollectionPatchData = (
  formValues: Record<string, unknown>,
  attrMap: Map<string, AttributeSchema>,
  targetRow?: { values: AttributeValue[] }
) => {
  let attrToValueMap: Record<string, AttributeValue | AttributeValue[]> = {};

  if (targetRow) {
    attrToValueMap = targetRow.values.reduce((acc, value) => {
      const existing = acc[value.attribute_id];
      acc[value.attribute_id] = existing
        ? [...(Array.isArray(existing) ? existing : [existing]), value]
        : value;
      return acc;
    }, {} as ExistingAttributeMap);
  }
  return buildPatchData(formValues, (attribute_id) => {
    const attribute = attrMap.get(attribute_id);
    if (!attribute) {
      return null;
    }

    const attrValue = attrToValueMap[attribute_id] as
      | AttributeValue
      | AttributeValue[]
      | undefined;

    return {
      attribute,
      existingValues: Array.isArray(attrValue)
        ? attrValue
        : attrValue
        ? [attrValue]
        : [],
      mode: targetRow ? "edit" : "add",
    };
  });
};
